import { useState, useMemo, useEffect } from 'react';
import { useParams } from 'react-router';
import { connect, useDispatch } from 'react-redux';
import { useUnmount } from 'react-use';
import type { Dispatch } from 'redux';

import { KnmVersionsEnum } from 'APIPackage/KnmModule/enums/KnmVersionsEnum';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IKnmPreview } from 'APIPackage/KnmSearchModule/interfaces/IKnmPreview';

import { PassportTabsEnum } from '../enums/PassportTabsEnum';
import styles from './KnmPreview.module.scss';
import { KnmHeader } from './KnmHeader';
import { KnmCommon } from './KnmCommon';
import { ErknmCommon } from './ErknmCommon';
import { PmCommon } from './PmCommon';
import { KnmControl } from './KnmControl';
import { KnmFace } from './KnmFace';
import { KnmResult } from './KnmResult';
import { KnmViolation } from './KnmViolation';
import { KnmProsecutor } from './KnmProsecutor';
import { ErknmControl } from './ErknmControl';
import { PmControl } from './PmControl';
import { ErknmFace } from './ErknmFace';
import { PmFace } from './PmFace';
import { PmResult } from './PmResult';
import { knmPreviewInitPage, knmPreviewSetPassportData } from '../actions/knmPreviewActions';
import { getKnmPreviewPassportData } from '../selectors/knmPreviewSelectors';
import { adminGetFetchSettings } from '../../../PersonalAccountPackage/AdminModule/actions/adminAction';
import { IAdminSetting } from '../../../APIPackage/AdminModule/interfaces/IAdminSetting';
import { getAdminFetchSettings } from '../../../PersonalAccountPackage/AdminModule/selectors/adminSelectors';

interface IKnmPreviewProps {
  passportData?: IKnmPreview;
  settings?: IAdminSetting[];
}

const KnmPreviewWithoutStore = ({ passportData, settings }: IKnmPreviewProps) => {
  const [activeTab, setActiveTab] = useState<PassportTabsEnum>(PassportTabsEnum.COMMON);
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const { id } = useParams<{ id: string }>();
  const isErknm = passportData?.version === KnmVersionsEnum.ERKNM;
  const isPm = isErknm && !!passportData?.pmErknm;

  const hasViolation = useMemo(() => {
    const isObjects = !!passportData?.objects?.length;
    const isViolations = !!passportData?.objects?.some(obj => {
      return !!obj?.result?.violations?.length;
    });

    return isObjects && isViolations;
  }, [passportData]);

  useEffect(() => {
    dispatch(knmPreviewInitPage(id));
    dispatch(adminGetFetchSettings());
  }, [id, dispatch]);

  useUnmount(() => {
    dispatch(knmPreviewSetPassportData());
  });

  return (
    <div className={styles.Page}>
      {passportData && (
        <>
          <KnmHeader
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            hasViolation={hasViolation}
            isErknm={isErknm}
            isPm={isPm}
            resultOfInspectionPm={passportData?.pmErknm?.resultOfInspection}
            passportData={passportData}
          />
          <div className={styles.KnnWrapperContent}>
            {activeTab === PassportTabsEnum.COMMON &&
              (isErknm ? (
                (isPm && <PmCommon passportData={passportData} />) || (
                  <ErknmCommon passportData={passportData} settings={settings} />
                )
              ) : (
                <KnmCommon passportData={passportData} />
              ))}
            {activeTab === PassportTabsEnum.CONTROL &&
              (isErknm ? (
                (isPm && <PmControl passportData={passportData} />) || <ErknmControl passportData={passportData} />
              ) : (
                <KnmControl passportData={passportData} />
              ))}
            {activeTab === PassportTabsEnum.PROSECUTOR && <KnmProsecutor passportData={passportData} />}
            {activeTab === PassportTabsEnum.FACE &&
              (isErknm ? (
                (isPm && <PmFace passportData={passportData} />) || <ErknmFace passportData={passportData} />
              ) : (
                <KnmFace passportData={passportData} />
              ))}
            {activeTab === PassportTabsEnum.RESULT &&
              (isPm ? <PmResult passportData={passportData} /> : <KnmResult passportData={passportData} />)}
            {activeTab === PassportTabsEnum.VIOLATION && <KnmViolation passportData={passportData} />}
          </div>
        </>
      )}
    </div>
  );
};

const mapStateToProps = (state: IRootState) => ({
  passportData: getKnmPreviewPassportData(state),
  settings: getAdminFetchSettings(state),
});

export const KnmPreview = connect(mapStateToProps)(KnmPreviewWithoutStore);
